/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/bolmatePeriodSelector.scss';
import {AwesomeIcon, hasHover, remToPx} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';

import classNames from 'classnames';

import React, {CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {PeriodLabel, translatePeriodLabel} from '../../../util/enums';

import {BolmateControlShape, BolmateControlSize} from '../../../util/types';

import BolmateButton from '../button/bolmateButton';

import DvrdDatePicker from '../date/dvrdDatePicker';

const DEFAULT_PRESET = PeriodLabel.LAST_30_DAYS;

interface Props {
    onChange: (range: [IDate, IDate], presetLabel: PeriodLabel | null) => void;
    value?: [IDate | null, IDate | null];
    selectedPreset?: PeriodLabel | null;
    disabled?: boolean;
    className?: string;
    label?: string;
    min?: IDate | null;
    max?: IDate | null;
    selectSize?: BolmateControlSize;
    selectShape?: BolmateControlShape;
}

interface PresetPeriod {
    label: PeriodLabel;
    getRange: () => [IDate | null, IDate | null];
}

const PRESET_PERIODS: Array<PresetPeriod> = [
    {
        label: PeriodLabel.TODAY,
        getRange: () => [IDate.now().startOf('day'), IDate.now().endOf('day')]
    },
    {
        label: PeriodLabel.YESTERDAY,
        getRange: () => [IDate.now().subtract(1, 'day').startOf('day'), IDate.now().subtract(1, 'day').endOf('day')]
    },
    {
        label: PeriodLabel.LAST_7_DAYS,
        getRange: () => [IDate.now().subtract(7, 'days').startOf('day'), IDate.now().endOf('day')]
    },
    {
        label: PeriodLabel.LAST_14_DAYS,
        getRange: () => [IDate.now().subtract(14, 'days').startOf('day'), IDate.now().endOf('day')]
    },
    {
        label: PeriodLabel.LAST_30_DAYS,
        getRange: () => [IDate.now().subtract(30, 'days').startOf('day'), IDate.now().endOf('day')]
    },
    {
        label: PeriodLabel.LAST_90_DAYS,
        getRange: () => [IDate.now().subtract(90, 'days').startOf('day'), IDate.now().endOf('day')]
    },
    {
        label: PeriodLabel.ALL,
        getRange: () => [null, null]
    }
];

function BolmatePeriodSelector(props: Props) {
    const {t} = useTranslation();
    const {
        onChange,
        value,
        selectedPreset,
        disabled,
        className,
        label,
        min,
        max,
        selectShape = 'square',
        selectSize = 'normal'
    } = props;
    const [open, setOpen] = useState(false);
    const [customMode, setCustomMode] = useState(false);
    const [customStart, setCustomStart] = useState<IDate | null>(null);
    const [customEnd, setCustomEnd] = useState<IDate | null>(null);
    const [internalPreset, setInternalPreset] = useState<PeriodLabel | null>(DEFAULT_PRESET);
    const [startPickerKey, setStartPickerKey] = useState(0);
    const [endPickerKey, setEndPickerKey] = useState(0);
    const [itemsWidth, setItemsWidth] = useState<number | null>(null);
    const rootStyle: CSSProperties | undefined = useMemo(() => {
        if (itemsWidth === null) return undefined;
        const rootWidth = itemsWidth + remToPx(0.75);
        return {width: rootWidth + 'px'};
    }, [itemsWidth]);
    const container = useRef<HTMLDivElement>(null);
    const itemsRef = useRef<HTMLDivElement>(null);
    const mounted = useRef(false);

    const effectivePreset = selectedPreset !== undefined ? selectedPreset : internalPreset;

    const displayLabel = useMemo(() => {
        if (customMode) return t('controls.periodSelector.customEllipsis', 'Aangepast...');
        if (effectivePreset) return translatePeriodLabel(effectivePreset, t);
        if (!value) return t('controls.periodSelector.selectPeriod', 'Selecteer periode');
        const [start, end] = value;
        if (!start && !end) return t('controls.periodSelector.all', 'Alles');
        if (!start || !end) return t('controls.periodSelector.selectPeriod', 'Selecteer periode');
        return t('controls.periodSelector.custom', 'Aangepast');
    }, [value, effectivePreset, customMode, t]);

    function onClickElement() {
        if (disabled) return;
        if (!open) {
            setCustomMode(effectivePreset === null);
        }
        setOpen(!open);
    }

    function onClickPreset(preset: PresetPeriod) {
        return function(evt: React.MouseEvent) {
            evt.stopPropagation();
            const [start, end] = preset.getRange();
            if (start && end) {
                onChange([start, end], preset.label);
            } else {
                onChange([IDate.now().subtract(10, 'years').startOf('day'), IDate.now().endOf('day')], preset.label);
            }
            setInternalPreset(preset.label);
            setOpen(false);
        };
    }

    function onClickCustom(evt: React.MouseEvent) {
        evt.stopPropagation();
        if (value) {
            const [start, end] = value;
            setCustomStart(start);
            setCustomEnd(end);
        } else {
            setCustomStart(IDate.now().subtract(1, 'month').startOf('day'));
            setCustomEnd(IDate.now().endOf('day'));
        }
        setCustomMode(true);
    }

    function onClickBack(evt: React.MouseEvent) {
        evt.stopPropagation();
        setCustomMode(false);
    }

    function onChangeCustomStart(value: IDate | null) {
        setCustomStart(value ? value.startOf('day') : null);
    }

    function onChangeCustomEnd(value: IDate | null) {
        setCustomEnd(value ? value.endOf('day') : null);
    }

    function onApplyRange(evt: React.MouseEvent) {
        evt.stopPropagation();
        if (!customStart || !customEnd) return;
        onChange([customStart, customEnd], null);
        setInternalPreset(null);
        setOpen(false);
    }

    function determineItemsWidth() {
        if (!itemsRef.current) return null;
        const nextWidth = itemsRef.current.getBoundingClientRect().width;
        setItemsWidth(prev => (prev === nextWidth ? prev : nextWidth));
    }

    function renderLabel() {
        if (!label) return null;
        return (
            <label className='bolmate-period-selector-label' onClick={onClickElement}>
                {label}
            </label>
        );
    }

    function renderValue() {
        return (
            <label className='bolmate-period-selector-value' onClick={onClickElement}>
                {displayLabel}
            </label>
        );
    }

    function renderArrow() {
        return (
            <AwesomeIcon
                name='chevron-down'
                onClick={onClickElement}
                className={classNames('bolmate-period-selector-arrow', open && 'open')}
            />
        );
    }

    function renderPresetItems() {
        const isCustomActive = effectivePreset === null;
        return (
            <>
                {PRESET_PERIODS.map((preset, index) => {
                    const selected = effectivePreset === preset.label;
                    return (
                        <label
                            key={index}
                            className={classNames('bolmate-period-selector-item', selected && 'selected')}
                            onClick={onClickPreset(preset)}
                        >
                            <span>{translatePeriodLabel(preset.label, t)}</span>
                            {selected && <AwesomeIcon name='check' className='bolmate-period-selector-check'/>}
                        </label>
                    );
                })}
                <label
                    className={classNames('bolmate-period-selector-item custom', isCustomActive && 'selected')}
                    onClick={onClickCustom}
                >
                    <span>{t('controls.periodSelector.customEllipsis', 'Aangepast...')}</span>
                    {isCustomActive && <AwesomeIcon name='check' className='bolmate-period-selector-check'/>}
                </label>
            </>
        );
    }

    function resetPickers() {
        setStartPickerKey(k => k + 1);
        setEndPickerKey(k => k + 1);
    }

    function renderCustomRangeView() {
        const canApply = !!customStart && !!customEnd;
        return (
            <div
                className='custom-range-view'
                onClickCapture={(e: React.MouseEvent<HTMLDivElement>) => {
                    if (!(e.target as HTMLElement).closest('.dvrd-date-picker')) {
                        resetPickers();
                    }
                }}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                }}
            >
                <div className='custom-range-header'>
                    <span className='custom-range-title'>
                        {t('controls.periodSelector.customRange', 'Aangepast bereik')}
                    </span>
                    <span className='custom-range-back' onClick={onClickBack}>
                        {t('controls.periodSelector.back', 'Terug')}
                    </span>
                </div>
                <div
                    onClickCapture={() => setEndPickerKey(k => k + 1)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                    <DvrdDatePicker
                        key={startPickerKey}
                        onChange={onChangeCustomStart}
                        value={customStart}
                        label={t('controls.periodSelector.startDate', 'Startdatum')}
                        placeholder={t('controls.periodSelector.datePlaceholder', 'dd-mm-jjjj')}
                        dateFormat='DD-MM-YYYY'
                        min={min}
                        max={customEnd ?? max}
                        allowBodyScroll
                        closeOnChange
                    />
                </div>
                <div
                    onClickCapture={() => setStartPickerKey(k => k + 1)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                    <DvrdDatePicker
                        key={endPickerKey}
                        onChange={onChangeCustomEnd}
                        value={customEnd}
                        label={t('controls.periodSelector.endDate', 'Einddatum')}
                        placeholder={t('controls.periodSelector.datePlaceholder', 'dd-mm-jjjj')}
                        dateFormat='DD-MM-YYYY'
                        min={customStart ?? min}
                        max={max}
                        allowBodyScroll
                        closeOnChange
                    />
                </div>
                <BolmateButton
                    label={t('controls.periodSelector.applyRange', 'Bereik toepassen')}
                    onClick={onApplyRange}
                    color='blue'
                    size='small'
                    align='center'
                    disabled={!canApply}
                    className='custom-range-apply-btn'
                />
            </div>
        );
    }

    function renderItems() {
        return (
            <div
                className={classNames('bolmate-period-selector-items', open && !disabled && 'open', 'bottom')}
                ref={itemsRef}
            >
                {customMode ? renderCustomRangeView() : renderPresetItems()}
            </div>
        );
    }

    function clickListener() {
        if (!hasHover(container.current)) {
            setOpen(false);
        }
    }

    const repositionCalendars = useCallback(() => {
        requestAnimationFrame(() => {
            const el = container.current;
            if (!el) return;
            const datePickers = el.querySelectorAll('.custom-range-view .dvrd-date-picker');
            datePickers.forEach(dp => {
                const bg = dp.querySelector('.with-background') as HTMLElement | null;
                if (!bg) return;
                const pickerEl = bg.querySelector('.picker') as HTMLElement | null;
                if (!pickerEl) return;
                const pH = pickerEl.offsetHeight;
                const pW = pickerEl.offsetWidth;
                if (!pH || !pW) return;

                const anchor = dp.querySelector('.value-container') as HTMLElement | null;
                if (!anchor) return;
                const rect = anchor.getBoundingClientRect();
                const vh = window.innerHeight;
                const vw = window.innerWidth;
                const gap = 4;
                const buffer = 16; // 1rem

                let top: number;
                let left: number;

                const spaceBelow = vh - rect.bottom;
                const spaceRight = vw - rect.right;
                const spaceLeft = rect.left;

                if (spaceBelow >= pH + gap * 2 + buffer) {
                    // Below input
                    top = rect.bottom + gap;
                    left = rect.left;
                } else if (spaceRight >= pW + gap * 2 + buffer) {
                    // Right of input
                    top = rect.top;
                    left = rect.right + gap;
                } else if (spaceLeft >= pW + gap * 2 + buffer) {
                    // Left of input
                    top = rect.top;
                    left = rect.left - pW - gap;
                } else {
                    // Fallback: side with most horizontal space
                    if (spaceRight >= spaceLeft) {
                        left = Math.max(gap, vw - pW - gap);
                        top = rect.top;
                    } else {
                        left = gap;
                        top = rect.top;
                    }
                }

                // Clamp to viewport
                if (top + pH > vh - gap) top = Math.max(gap, vh - pH - gap);
                if (top < gap) top = gap;
                if (left + pW > vw - gap) left = Math.max(gap, vw - pW - gap);
                if (left < gap) left = gap;

                bg.style.setProperty('top', `${top}px`, 'important');
                bg.style.setProperty('left', `${left}px`, 'important');
            });
        });
    }, []);

    const closePickers = useCallback(() => {
        setStartPickerKey(k => k + 1);
        setEndPickerKey(k => k + 1);
    }, []);

    useEffect(() => {
        if (mounted.current) return;
        mounted.current = true;
        if (value) return;
        const preset = PRESET_PERIODS.find(p => p.label === DEFAULT_PRESET);
        if (!preset) return;
        const [start, end] = preset.getRange();
        if (start && end) onChange([start, end], DEFAULT_PRESET);
    }, []);

    useEffect(() => {
        if (!open) {
            setCustomMode(false);
        }
        window.setTimeout(() => {
            if (open) document.addEventListener('click', clickListener);
            else document.removeEventListener('click', clickListener);
        });
    }, [open]);

    useEffect(() => {
        if (!customMode || !open) return;
        const el = container.current;
        if (!el) return;

        const observer = new MutationObserver(repositionCalendars);
        observer.observe(el, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style']});

        const page = document.querySelector('div.page');
        if (page)
            page.addEventListener('scroll', closePickers, true);
        window.addEventListener('resize', repositionCalendars);
        repositionCalendars();

        return () => {
            observer.disconnect();
            const page = document.querySelector('div.page');
            if (page)
                page.removeEventListener('scroll', closePickers, true);
            window.removeEventListener('resize', repositionCalendars);
        };
    }, [customMode, open, repositionCalendars, closePickers]);

    useLayoutEffect(() => {
        determineItemsWidth();
    }, [customMode, open]);

    return (
        <div
            className={classNames(
                'bolmate-period-selector',
                disabled && 'disabled',
                open && 'open',
                className,
                `shape-${selectShape}`,
                `size-${selectSize}`
            )}
            ref={container}
            onClick={onClickElement}
            style={rootStyle}
        >
            {renderLabel()}
            <div className='content-container'>
                {renderValue()}
                {renderArrow()}
            </div>
            {renderItems()}
        </div>
    );
}

export default BolmatePeriodSelector;
